import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, CaretRight, Stethoscope } from "@phosphor-icons/react";
import { ALL_STATES, ALL_SPECIALTIES } from "../lib/seoContent";

const STATE_REGIONS = {
  "Northeast": ["CT", "DE", "ME", "MD", "MA", "NH", "NJ", "NY", "PA", "RI", "VT", "DC"],
  "Southeast": ["AL", "AR", "FL", "GA", "KY", "LA", "MS", "NC", "SC", "TN", "VA", "WV"],
  "Midwest": ["IL", "IN", "IA", "KS", "MI", "MN", "MO", "NE", "ND", "OH", "SD", "WI"],
  "West": ["AK", "AZ", "CA", "CO", "HI", "ID", "MT", "NV", "NM", "OR", "UT", "WA", "WY"],
  "Southwest": ["TX", "OK"],
};

const StatesLanding = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Healthcare Providers by State",
    "description": "Browse healthcare providers in every US state. Find doctors, specialists, and healthcare organizations with verified NPI numbers.",
    "url": window.location.href,
  };

  return (
    <>
      <Helmet>
        <title>NPI Registry by State — Find Provider NPI Numbers | NPI Lookup</title>
        <meta name="description" content="Browse the national NPI registry by US state. Find doctors, hospitals, and healthcare providers with verified NPI numbers, specialties, and NPPES contact information in all 50 states." />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://npilookup.com/" },
            { "@type": "ListItem", "position": 2, "name": "Browse by State" }
          ]
        })}</script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        {/* Hero Section */}
        <div className="bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <nav className="mb-6 text-sm">
              <ol className="flex items-center gap-2 text-slate-400">
                <li><Link to="/" className="hover:text-white">Home</Link></li>
                <li>/</li>
                <li className="text-white font-medium">Browse by State</li>
              </ol>
            </nav>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-teal rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-white" weight="bold" />
              </div>
              <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white" data-testid="states-landing-title">
                Browse by State
              </h1>
            </div>
            <p className="text-slate-300 text-lg max-w-2xl">
              Find healthcare providers in your state. Select a state below to browse registered doctors, specialists, and healthcare organizations.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* States by Region */}
          {Object.entries(STATE_REGIONS).map(([region, codes]) => (
            <section key={region} className="mb-8" data-testid={`region-${region.toLowerCase()}`}>
              <h2 className="font-display font-bold text-xl text-slate-900 mb-4">
                {region}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {codes.map(code => {
                  const st = ALL_STATES.find(s => s.code === code);
                  if (!st) return null;
                  return (
                    <Link
                      key={code}
                      to={`/state/${code}`}
                      className="group bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md hover:border-teal/30 transition-all text-center"
                      data-testid={`state-card-${code}`}
                    >
                      <p className="font-display font-bold text-2xl text-navy group-hover:text-teal transition-colors">
                        {code}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">{st.name}</p>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}

          {/* Quick Specialty + State Combinations */}
          <section className="mt-12" data-testid="popular-combinations">
            <h2 className="font-display font-bold text-2xl text-slate-900 mb-2">
              Popular Specialty & State Combinations
            </h2>
            <p className="text-slate-500 mb-6">
              Jump directly to commonly searched specialty and state combinations
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { specialty: "cardiology", state: "CA", label: "Cardiologists in California" },
                { specialty: "family-medicine", state: "TX", label: "Family Medicine in Texas" },
                { specialty: "dermatology", state: "NY", label: "Dermatologists in New York" },
                { specialty: "orthopedics", state: "FL", label: "Orthopedic Surgeons in Florida" },
                { specialty: "psychiatry", state: "CA", label: "Psychiatrists in California" },
                { specialty: "pediatrics", state: "IL", label: "Pediatricians in Illinois" },
                { specialty: "neurology", state: "PA", label: "Neurologists in Pennsylvania" },
                { specialty: "internal-medicine", state: "OH", label: "Internal Medicine in Ohio" },
                { specialty: "surgery", state: "TX", label: "Surgeons in Texas" },
              ].map(combo => (
                <Link
                  key={combo.label}
                  to={`/specialty/${combo.specialty}/${combo.state.toLowerCase()}`}
                  className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200 hover:border-teal/30 hover:bg-teal/5 text-sm text-slate-700 hover:text-teal transition-all"
                >
                  <span>{combo.label}</span>
                  <CaretRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </section>

          {/* Browse Specialties Grid */}
          <section className="mt-12" data-testid="browse-specialties-section">
            <h2 className="font-display font-bold text-xl text-slate-900 mb-4">
              <span className="flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-teal" />
                Or Browse by Specialty
              </span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {ALL_SPECIALTIES.map(s => (
                <Link
                  key={s.slug}
                  to={`/specialty/${s.slug}`}
                  className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200 hover:border-teal/30 hover:bg-teal/5 text-sm text-slate-700 hover:text-teal transition-all"
                >
                  <span>{s.name}</span>
                  <CaretRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </section>

          {/* SEO Content */}
          <section className="mt-12 mb-8" data-testid="states-seo-content">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8">
              <h2 className="font-display font-bold text-xl text-slate-900 mb-4">
                Finding Healthcare Providers by Location
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  The United States has over 6.2 million registered healthcare providers across all 50 states
                  and the District of Columbia. Each provider is assigned a unique National Provider Identifier
                  (NPI) number by the Centers for Medicare & Medicaid Services (CMS), which serves as their
                  official identification in the healthcare system.
                </p>
                <p>
                  Our state directory allows you to browse providers by location, making it easy to find
                  healthcare professionals near you. Each state page shows providers practicing in that
                  state, with options to filter by specialty. You can also explore specific cities within
                  each state for more localized results.
                </p>
                <p>
                  Whether you are looking for a primary care physician, a specialist, or a healthcare
                  organization, our directory provides verified NPI numbers, practice addresses, credentials,
                  and contact information to help you make informed healthcare decisions.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default StatesLanding;
